/**
 * Pergunta serviço fiscal, profissional e descrição antes de emitir a NFSe.
 *
 * Quem chama passa o contexto da cobrança e a função que emite. Se não houver
 * nada para escolher (sem serviços fiscais nem profissionais, ou a busca falhar),
 * a emissão acontece direto como antes; caso contrário abre o diálogo com a
 * sugestão baseada nos procedimentos.
 */

import React, { useCallback, useState } from 'react';
import NfseServiceCodeDialog, {
  fetchNfseServiceSuggestion,
  type NfseEmitChoice,
  type NfseServiceContext,
  type NfseServiceSuggestion,
} from '../components/clinic/NfseServiceCodeDialog';
import { todayYmdBr } from '../utils/localCalendarDate';

type EmitFn = (choice: NfseEmitChoice) => Promise<void> | void;

interface Pending {
  context: NfseServiceContext;
  preloaded: NfseServiceSuggestion | null;
  emit: EmitFn;
}

export function useNfseServiceCodePrompt() {
  const [pending, setPending] = useState<Pending | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const promptAndEmit = useCallback(async (context: NfseServiceContext, emit: EmitFn) => {
    let suggestion: NfseServiceSuggestion | null = null;
    try {
      suggestion = await fetchNfseServiceSuggestion(context);
    } catch {
      suggestion = null;
    }

    if (!suggestion?.options?.length && !suggestion?.description?.professionals?.length) {
      await emit({ service_code_id: null, issue_date: todayYmdBr() });
      return;
    }
    setPending({ context, preloaded: suggestion, emit });
  }, []);

  const nfseServiceCodeDialog = pending ? (
    <NfseServiceCodeDialog
      open
      context={pending.context}
      preloaded={pending.preloaded}
      submitting={submitting}
      onClose={() => { if (!submitting) setPending(null); }}
      onConfirm={async (choice) => {
        setSubmitting(true);
        try {
          await pending.emit(choice);
          setPending(null);
        } finally {
          setSubmitting(false);
        }
      }}
    />
  ) : null;

  return { promptAndEmit, nfseServiceCodeDialog };
}

export default useNfseServiceCodePrompt;
